import React, { useState, useEffect } from 'react';
import { 
  Camera, Activity, Radio, AlertCircle, RefreshCw, 
  TrendingUp, Wifi, WifiOff, Eye, Users, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  CartesianGrid 
} from 'recharts';
import api from '@/lib/api';

// Stat card for live metrics
const StatCard = ({ title, value, icon: Icon, description, color }) => (
  <Card 
    className="border-border bg-card"
    data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">
        {title}
      </CardTitle>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-bold tracking-tight">{value}</div>
      {description && (
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      )}
    </CardContent>
  </Card>
);

// Skeleton loader for cards
const SkeletonCard = () => (
  <Card className="border-border bg-card">
    <CardHeader className="pb-2">
      <div className="h-4 w-24 animate-pulse rounded bg-muted" />
    </CardHeader>
    <CardContent>
      <div className="h-8 w-16 animate-pulse rounded bg-muted" />
      <div className="mt-2 h-3 w-32 animate-pulse rounded bg-muted" />
    </CardContent>
  </Card>
);

// Skeleton for chart
const SkeletonChart = () => (
  <Card className="border-border bg-card">
    <CardHeader className="pb-2">
      <div className="h-5 w-48 animate-pulse rounded bg-muted" />
    </CardHeader>
    <CardContent>
      <div className="h-48 w-full animate-pulse rounded bg-muted" />
    </CardContent>
  </Card>
);

// 7-day trend chart component
const TrendChart = ({ title, data, total, dataKey, color, emptyMessage }) => {
  const hasData = data && data.length > 0 && data.some(d => d[dataKey] > 0);
  
  return (
    <Card className="border-border bg-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium">{title}</CardTitle>
          {total != null && (
            <span className="text-sm font-mono text-muted-foreground">
              {total.toFixed(1)}h total
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
            {emptyMessage || 'No daily trend data available'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis 
                dataKey="day" 
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}h`}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
                formatter={(value) => [`${value.toFixed(1)}h`, 'Hours']}
              />
              <Bar 
                dataKey={dataKey} 
                fill={color} 
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Format hours with 1 decimal
const formatHours = (hours) => {
  if (hours == null || isNaN(hours)) return '0.0';
  return Number(hours).toFixed(1);
};

export function Dashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [topCameras, setTopCameras] = useState([]);
  const [cameraStatus, setCameraStatus] = useState({ online: 0, offline: 0, total: 0 });
  const [dailyWatchData, setDailyWatchData] = useState([]);
  const [dailyListenData, setDailyListenData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [cameraSegment, setCameraSegment] = useState('all');

  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch all data in parallel
      const [statsData, analyticsData, camerasAnalyticsData, camerasData] = await Promise.all([
        api.getStats().catch(err => ({ _error: err.message })),
        api.getAnalyticsSummary('7d').catch(err => ({ _error: err.message })),
        api.getCamerasAnalytics('7d').catch(err => ({ _error: err.message })),
        api.getCameras().catch(err => ({ _error: err.message })),
      ]);

      // Check if all requests failed
      const allFailed = [statsData, analyticsData, camerasAnalyticsData, camerasData]
        .every(d => d?._error);
      
      if (allFailed) {
        throw new Error('All API requests failed. Please check your connection.');
      }

      // Set stats (if successful)
      if (statsData && !statsData._error) {
        setStats(statsData);
      }

      // Set analytics summary and extract daily data if available
      if (analyticsData && !analyticsData._error) {
        setAnalytics(analyticsData);
        
        // Production API has daily_breakdown at root level with combined camera/radio data
        const dailyBreakdown = analyticsData?.daily_breakdown 
          || analyticsData?.by_day 
          || [];
        
        // Format daily watch data for charts - use camera_watch_hours field
        if (dailyBreakdown.length > 0) {
          setDailyWatchData(dailyBreakdown.map(d => ({
            day: formatDayLabel(d.date || d.day),
            hours: d.camera_watch_hours ?? d.watch_hours ?? d.hours ?? 0
          })));
          
          // Format daily listen data for charts - use radio_listen_hours field
          setDailyListenData(dailyBreakdown.map(d => ({
            day: formatDayLabel(d.date || d.day),
            hours: d.radio_listen_hours ?? d.listen_hours ?? 0
          })));
        } else {
          setDailyWatchData([]);
          setDailyListenData([]);
        }
      }

      // Set top 5 cameras by watch hours
      if (camerasAnalyticsData && !camerasAnalyticsData._error) {
        const cameras = Array.isArray(camerasAnalyticsData) 
          ? camerasAnalyticsData 
          : camerasAnalyticsData.cameras || [];
        
        // Sort by watch hours and take top 5
        const sorted = [...cameras]
          .sort((a, b) => (b.total_watch_hours || b.watch_hours || 0) - (a.total_watch_hours || a.watch_hours || 0))
          .slice(0, 5);
        setTopCameras(sorted);
      }

      // Calculate online/offline camera counts
      if (camerasData && !camerasData._error) {
        const camerasList = Array.isArray(camerasData) 
          ? camerasData 
          : camerasData.cameras || [];
        
        const online = camerasList.filter(c => c.status === 'online').length;
        const offline = camerasList.filter(c => c.status === 'offline').length;
        setCameraStatus({ online, offline, total: camerasList.length });
      }

      setLastRefresh(new Date());

    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format day label from date string
  const formatDayLabel = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="dashboard-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">RailStream system overview</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <SkeletonChart />
          <SkeletonChart />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6" data-testid="dashboard-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">RailStream system overview</p>
          </div>
        </div>
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center gap-4 p-6">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div className="flex-1">
              <p className="font-medium text-foreground">Dashboard failed to load</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button 
              variant="outline" 
              onClick={fetchDashboardData}
              data-testid="retry-stats-btn"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Extract live/current values from API responses
  // Production API uses 'cameras' not 'cameras_count'
  const camerasTotal = stats?.cameras ?? stats?.cameras_count ?? cameraStatus.total ?? 0;
  const activeSessions = stats?.sessions?.active ?? stats?.active_sessions ?? 0;
  const activeUniqueUsers = stats?.sessions?.active_users ?? stats?.active_unique_users ?? 0;
  
  // Radio active sessions (if available separately)
  // Production API uses sessions.active_radio
  const radioActiveSessions = stats?.sessions?.active_radio 
    ?? stats?.sessions?.radio_active 
    ?? stats?.radio_active_sessions 
    ?? stats?.radio?.active 
    ?? null;
  
  // Camera vs Radio breakdown for active sessions (if available)
  // Production API uses sessions.active_camera
  const cameraActiveSessions = stats?.sessions?.active_camera
    ?? stats?.sessions?.camera_active 
    ?? stats?.camera_active_sessions 
    ?? (radioActiveSessions != null ? activeSessions - radioActiveSessions : null);

  // Analytics totals (7d)
  const watchHoursTotal = analytics?.cameras?.total_watch_hours ?? 0;
  const listenHoursTotal = analytics?.radio?.total_listen_hours ?? 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">RailStream system overview</p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-muted-foreground hidden sm:block">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchDashboardData}
            data-testid="refresh-stats-btn"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Live Status Row - Current operational metrics */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Cameras"
          value={camerasTotal}
          icon={Camera}
          description={`${cameraStatus.online} online / ${cameraStatus.offline} offline`}
          color="bg-primary/10 text-primary"
        />
        <StatCard
          title="Active Sessions"
          value={activeSessions}
          icon={Activity}
          description={cameraActiveSessions != null && radioActiveSessions != null 
            ? `${cameraActiveSessions} camera / ${radioActiveSessions} radio`
            : 'Currently streaming'}
          color="bg-amber-500/10 text-amber-500"
        />
        <StatCard
          title="Unique Viewers"
          value={activeUniqueUsers}
          icon={Users}
          description="Active unique users"
          color="bg-orange-500/10 text-orange-500"
        />
        <StatCard
          title="Radio Active"
          value={radioActiveSessions ?? '—'}
          icon={Radio}
          description={radioActiveSessions != null ? 'Radio streams' : 'Not available separately'}
          color="bg-violet-500/10 text-violet-500"
        />
      </div>

      {/* 7-Day Trend Charts */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <TrendChart
          title="Watch Hours by Day (7d)"
          data={dailyWatchData}
          total={watchHoursTotal}
          dataKey="hours"
          color="hsl(var(--primary))"
          emptyMessage="No daily watch data available"
        />
        <TrendChart
          title="Listen Hours by Day (7d)"
          data={dailyListenData}
          total={listenHoursTotal}
          dataKey="hours"
          color="hsl(262, 83%, 58%)"
          emptyMessage="No daily listen data available"
        />
      </div>

      {/* Top 5 Cameras by Watch Hours */}
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5 text-primary" />
              Top 5 Cameras by Watch Hours (7d)
            </CardTitle>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select 
                value={cameraSegment} 
                onValueChange={setCameraSegment}
                disabled={true}
              >
                <SelectTrigger className="w-32 h-8 text-xs" data-testid="camera-segment-select">
                  <SelectValue placeholder="Segment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="mobile" disabled>Mobile</SelectItem>
                  <SelectItem value="website" disabled>Website</SelectItem>
                  <SelectItem value="ott" disabled>OTT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {topCameras.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No camera analytics data available
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-3 px-4 text-left font-medium text-muted-foreground w-12">#</th>
                    <th className="py-3 px-4 text-left font-medium text-muted-foreground">Camera Name</th>
                    <th className="py-3 px-4 text-right font-medium text-muted-foreground w-32">Watch Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {topCameras.map((camera, index) => (
                    <tr 
                      key={camera._id || camera.id || camera.camera_id || index} 
                      className="border-b border-border/50 hover:bg-secondary/30 transition-colors"
                    >
                      <td className="py-3 px-4 font-mono text-muted-foreground">{index + 1}</td>
                      <td className="py-3 px-4 font-medium">
                        {camera.name || camera.camera_name || camera.camera_id || 'Unknown'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono">
                        {formatHours(camera.total_watch_hours || camera.watch_hours || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Status */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg">System Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-3">
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Wifi className="h-4 w-4 text-green-500" />
              Online Cameras
            </span>
            <span className="text-sm font-bold text-green-500">
              {cameraStatus.online}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-3">
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <WifiOff className="h-4 w-4 text-red-500" />
              Offline Cameras
            </span>
            <span className="text-sm font-bold text-red-500">
              {cameraStatus.offline}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-3">
            <span className="text-sm text-muted-foreground">API Server</span>
            <span className="text-sm font-mono text-muted-foreground">api.railstream.net</span>
          </div>
          {lastRefresh && (
            <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-3">
              <span className="text-sm text-muted-foreground">Last Refresh</span>
              <span className="text-sm font-mono text-muted-foreground">
                {lastRefresh.toLocaleString()}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Dashboard;
